import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Paper,
    Grid,
    Chip,
    IconButton
} from '@mui/material';
import {
    PlayArrow as PlayIcon,
    Stop as StopIcon,
    Timer as TimerIcon,
    LocalFireDepartment as FireIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API_BASE_URL } from '../../config/api';

const MotionBox = motion(Box);

const FastingTimer = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [elapsedTime, setElapsedTime] = useState(0); // in seconds

    const fetchStatus = async () => {
        try {
            const token = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')).token : null;
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

            const response = await fetch(`${API_BASE_URL}/fasting/status`, { headers });
            if (response.ok) {
                const data = await response.json();
                setStatus(data);
                if (!data.in_fasting_window) { // Means we ARE fasting (confusing name in backend, fix logic later)
                    // Actually backend says: in_fasting_window=True means NOT fasting (eating window).
                    // Let's check logic: 
                    // Service: in_fasting_window = not is_fasting.
                    // So if is_fasting is True, in_fasting_window is False.
                    // Wait, "in_fasting_window" usually means "I am in the window where I fast".
                    // But the service logic was: `in_fasting_window=not is_fasting`.
                    // If is_fasting=True (I am fasting), then in_fasting_window=False? That sounds inverted.
                    // Let's assume: is_fasting = !status.in_fasting_window

                    // Actually, let's rely on current_fast_hours. If > 0, likely fasting.
                    // Or better, let's trust the boolean but verify interpretation.
                }
            }
        } catch (err) {
            console.error('Error fetching fasting status:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        const interval = setInterval(fetchStatus, 60000); // Update every minute
        return () => clearInterval(interval);
    }, []);

    // Local timer update for smooth UI
    useEffect(() => {
        let interval;
        if (status && !status.in_fasting_window) { // If fasting
            // Initialize elapsed time
            // We need start time to do this accurately locally, but for now we rely on API or simple increment
            // API returns current_fast_hours.
            setElapsedTime(status.current_fast_hours * 3600);

            interval = setInterval(() => {
                setElapsedTime(prev => prev + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [status]);

    const handleToggleFast = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')).token : null;
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const isFasting = !status.in_fasting_window;
            const endpoint = isFasting ? '/fasting/end' : '/fasting/start';

            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                method: 'POST',
                headers
            });

            if (response.ok) {
                await fetchStatus();
            }
        } catch (err) {
            console.error('Error toggling fast:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    if (loading && !status) {
        return <CircularProgress />;
    }

    const isFasting = !status?.in_fasting_window;
    const progress = Math.min((elapsedTime / (16 * 3600)) * 100, 100); // Target 16 hours

    return (
        <Paper sx={{ p: 4, borderRadius: 4, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '6px', background: isFasting ? 'linear-gradient(135deg, #1F5C3D 0%, #2E8B57 100%)' : '#e0e0e0' }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} color="text.secondary">
                    {isFasting ? 'AYUNO EN PROGRESO' : 'VENTANA DE ALIMENTACIÓN'}
                </Typography>
                <IconButton size="small"><SettingsIcon /></IconButton>
            </Box>

            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 4 }}>
                <CircularProgress
                    variant="determinate"
                    value={100}
                    size={240}
                    thickness={2}
                    sx={{ color: '#E3D0B0' }}
                />
                <CircularProgress
                    variant="determinate"
                    value={isFasting ? progress : 0}
                    size={240}
                    thickness={2}
                    sx={{
                        color: isFasting ? (status.autophagy_active ? '#2C7D8C' : '#2E8B57') : '#e0e0e0',
                        position: 'absolute',
                        left: 0,
                        strokeLinecap: 'round',
                    }}
                />
                <Box
                    sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <Typography variant="h3" component="div" fontWeight={700} color="text.primary">
                        {isFasting ? formatTime(elapsedTime) : '--:--:--'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {isFasting ? (status.autophagy_active ? '🔥 Autofagia Activa' : 'Quema de Grasa') : 'Listo para ayunar'}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleToggleFast}
                    startIcon={isFasting ? <StopIcon /> : <PlayIcon />}
                    color={isFasting ? 'warning' : 'primary'}
                    sx={{ borderRadius: 3, px: 4, py: 1.5, fontSize: '1.1rem' }}
                >
                    {isFasting ? 'Terminar Ayuno' : 'Iniciar Ayuno'}
                </Button>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#E3D0B0', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">INICIO</Typography>
                        <Typography variant="h6" fontWeight={600}>
                            {status?.window_start || '--:--'}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#E3D0B0', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">META</Typography>
                        <Typography variant="h6" fontWeight={600}>
                            16h
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default FastingTimer;
